import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import 'react18-json-view/src/dark.css';
import Iconify from '../../components/iconify';
import { checkResultForAction, decodeResult } from '../../utils/scraperScenarios';
import i18n from '../../i18n';

// Why core would refuse the result, by checkResultForAction's code. Literal keys,
// so the translation check can see them.
const REFUSAL_KEYS = {
    notText: 'scraperScenarios.checkNotText',
    payinNotObject: 'scraperScenarios.checkPayinNotObject',
    payinNoReference: 'scraperScenarios.checkPayinNoReference',
    payoutNotObject: 'scraperScenarios.checkPayoutNotObject'
};

/**
 * What a run returned: text (as a JSON tree when it parses as an object or
 * array), or the image. When the preview is for one of core's actions, it also
 * says whether core would accept this as that action's result — the same checks
 * its drivers make, so a scenario that "works" here but would fail every real
 * call is caught before it is saved.
 *
 * `done` is the done event: `{ result: { type, extension, encoding, data }, durationMs }`.
 */
export default function PreviewResult({ done, action, sx }) {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);
    const decoded = useMemo(() => decodeResult(done?.result), [done]);
    const check = checkResultForAction(action, decoded);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(decoded.text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            // No clipboard (an insecure origin): the text is selectable anyway.
        }
    };

    const meta = [
        done?.result?.type,
        done?.result?.extension,
        typeof done?.durationMs === 'number' ? `${(done.durationMs / 1000).toFixed(1)} s` : null
    ].filter(Boolean);

    return (
        <Stack spacing={1} sx={{ minHeight: 0, ...sx }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                    {meta.join(' · ')}
                </Typography>
                {decoded.kind === 'text' && (
                    <Button
                        size="small"
                        startIcon={<Iconify icon={copied ? 'eva:checkmark-outline' : 'eva:copy-outline'} />}
                        onClick={copy}
                    >
                        {copied ? i18n.t('text.copied') : i18n.t('text.copy')}
                    </Button>
                )}
            </Stack>

            {check && (
                <Alert severity={check.ok ? 'success' : 'warning'} sx={{ py: 0 }}>
                    {check.ok
                        ? i18n.t('scraperScenarios.checkOk', { action })
                        : i18n.t(REFUSAL_KEYS[check.code], { action })}
                </Alert>
            )}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    borderRadius: 1,
                    bgcolor: 'background.neutral',
                    p: 1.5
                }}
            >
                {decoded.kind === 'empty' && (
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t('scraperScenarios.resultEmpty')}
                    </Typography>
                )}
                {decoded.kind === 'image' && (
                    <Box
                        component="img"
                        src={decoded.src}
                        alt={i18n.t('scraperScenarios.resultImageAlt')}
                        sx={{ maxWidth: '100%', display: 'block' }}
                    />
                )}
                {decoded.kind === 'text' && decoded.json && (
                    <JsonView src={decoded.json} collapsed={3} dark={theme.palette.mode === 'dark'} />
                )}
                {decoded.kind === 'text' && !decoded.json && (
                    <Box
                        component="pre"
                        sx={{ m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                        {decoded.text}
                    </Box>
                )}
            </Box>
        </Stack>
    );
}

PreviewResult.propTypes = {
    done: PropTypes.object,
    action: PropTypes.string,
    sx: PropTypes.object
};
